import { SaneamentoProcessoComponent } from '../saneamento-processo/saneamento-processo.component';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { DocumentosService } from '../../core/services/documentos.service';
import { ProcessoService } from '../../core/services/processo.service';
import { SessionService } from '../../core/services/session.service';
import {
  AnaliseProcessoService,
  STATUS_BENEFICIO,
  TIPOS_DOCUMENTO_PECA,
} from '../../core/services/analise-processo.service';
import {
  AssinaturaLoteService,
  ProgressoAssinatura,
  mensagemResumoAssinatura,
} from '../../core/services/assinatura-lote.service';
import {
  TramitarDialogComponent,
  TramitarDialogResult,
} from './tramitar-dialog.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { DatasAtoSemEfeitoDialogComponent } from './datas-ato-sem-efeito-dialog.component';

@Component({
  standalone: true,
  selector: 'app-processo-detalhe',
  imports: [
    SaneamentoProcessoComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    NgxExtendedPdfViewerModule,
  ],
  templateUrl: './processo-detalhe.component.html',
  styleUrl: './processo-detalhe.component.scss',
})
export class ProcessoDetalheComponent implements OnInit, OnDestroy {
  public processo: any = null;
  public loading = false;

  public documentoAberto: any = null;
  public documentoUrl: SafeResourceUrl | null = null;
  public documentoBlob: Blob | null = null;
  public carregandoDocumento = false;
  private objectUrlAtual: string | null = null;

  public colunasTramitacoes: string[] = [
    'dataTramitacao',
    'localOrigemNome',
    'localDestinoNome',
    'usuario',
    'acoes',
  ];

  // Locais disponíveis para tramitar (carregados de GET /local-processo)
  public locaisDestino: { id: number; nome: string }[] = [];

  public assinandoLote = false;
  public progressoAssinatura: ProgressoAssinatura = { atual: 0, total: 0 };

  // --- Aba Peças ---
  public tiposPeca = TIPOS_DOCUMENTO_PECA;
  public pecaTipoSelecionado: string | null = null;
  public pecaArquivo: File | null = null;
  public inserindoPeca = false;
  public colunasPecas: string[] = ['nome', 'tipo', 'dataGeracao', 'assinado', 'acoes'];

  // --- Aba Análise Cearaprev ---
  public statusOpcoes = STATUS_BENEFICIO;
  public analiseStatus: string | null = null;
  public analiseDataPublicacao: string | null = null;
  public analiseDataRegistroTce: string | null = null;
  public salvandoAnalise = false;

  // --- Aba Histórico Status ---
  public colunasHistorico: string[] = ['dataStatus', 'statusAnalise', 'usuarioResponsavel'];

  /** Converte datas do backend para o formato aceito pelo input type=date. */
  private dataParaInput(valor: unknown): string | null {
    if (!valor) return null;
    const d = new Date(valor as string);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }

  constructor(
    private route: ActivatedRoute,
    private processoService: ProcessoService,
    private sessionService: SessionService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private assinaturaLoteService: AssinaturaLoteService,
    private documentosService: DocumentosService,
    private analiseProcessoService: AnaliseProcessoService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnDestroy(): void {
    this.revogarObjectUrl();
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.snackBar.open('ID do processo não informado', 'Fechar', { duration: 4000 });
      return;
    }
    this.carregarProcesso(id);
    this.carregarLocais();
  }

  private carregarLocais(): void {
    this.processoService.listarLocais().subscribe({
      next: (locais) => {
        this.locaisDestino = locais.map((l) => ({
          id: l.id,
          nome: l.descricao || l.nome,
        }));
      },
      error: (err) => {
        console.error('Falha ao listar locais de tramitação', err);
        this.snackBar.open(
          'Não foi possível carregar a lista de locais para tramitar.',
          'Fechar',
          { duration: 4000 }
        );
      },
    });
  }

  private carregarProcesso(id: string): void {
    this.loading = true;
    this.processoService.getProcessoCompleto(id).subscribe({
      next: (res) => {
        this.processo = res;
        // Campos da aba Análise, vindos do GET /processo/{id}/completo
        this.analiseStatus = res?.statusBeneficio ?? res?.status ?? null;
        this.analiseDataPublicacao = this.dataParaInput(res?.dataPublicacaoDO);
        this.analiseDataRegistroTce = this.dataParaInput(res?.dataRegistroTCE);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao carregar processo';
        this.snackBar.open(detalhe, 'Fechar', { duration: 4000 });
      },
    });
  }

  public voltar(): void {
    this.router.navigateByUrl('/pesquisar-processo');
  }

  public abrirDocumento(doc: any): void {
    // Peça inserida via mock: o PDF está em memória, abre sem ir ao backend.
    if (doc?.arquivoLocal) {
      this.revogarObjectUrl();
      this.documentoUrl = null;
      this.documentoAberto = doc;
      this.documentoBlob = doc.arquivoLocal;
      this.carregandoDocumento = false;
      return;
    }

    const hashAlvo = doc?.assinado && doc?.hashAssinado ? doc.hashAssinado : doc?.hash;
    if (!hashAlvo) {
      this.snackBar.open('Documento sem arquivo disponível para visualização.', 'Fechar', {
        duration: 4000,
      });
      return;
    }

    this.revogarObjectUrl();
    this.documentoUrl = null;
    this.documentoBlob = null;
    this.documentoAberto = doc;
    this.carregandoDocumento = true;

    this.documentosService.download(hashAlvo).subscribe({
      next: (blob) => {
        this.documentoBlob = new Blob([blob], { type: 'application/pdf' });
        this.carregandoDocumento = false;
      },
      error: (err) => {
        this.carregandoDocumento = false;
        this.documentoAberto = null;
        const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao carregar o documento.';
        this.snackBar.open(detalhe, 'Fechar', { duration: 4000 });
      },
    });
  }

  public fecharDocumento(): void {
    this.revogarObjectUrl();
    this.documentoAberto = null;
    this.documentoUrl = null;
    this.documentoBlob = null;
    this.carregandoDocumento = false;
  }

  public excluirDocumento(doc: any, event: Event): void {
    event.stopPropagation();

    // Peça mock (só em memória): remove localmente, sem ir ao backend.
    if (doc?.arquivoLocal) {
      const refMock = this.dialog.open(ConfirmDialogComponent, {
        data: {
          titulo: 'Excluir documento',
          mensagem: 'Deseja realmente excluir "' + (doc.nomeArquivo || 'este documento') + '"?',
          textoConfirmar: 'Excluir',
        },
        autoFocus: false,
      });
      refMock.afterClosed().subscribe((confirmado: boolean) => {
        if (!confirmado) return;
        if (this.documentoAberto === doc) this.fecharDocumento();
        this.processo.documentos = (this.processo.documentos || []).filter((d: any) => d !== doc);
        this.snackBar.open('Documento excluído.', 'Fechar', { duration: 3000 });
      });
      return;
    }

    if (!doc?.hash) {
      this.snackBar.open('Documento sem identificador para excluir.', 'Fechar', { duration: 4000 });
      return;
    }
    const nome = doc.nomeArquivo || doc.tipoDescricao || 'este documento';
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        titulo: 'Excluir documento',
        mensagem: 'Deseja realmente excluir "' + nome + '"?',
        textoConfirmar: 'Excluir',
      },
      autoFocus: false,
    });
    ref.afterClosed().subscribe((confirmado: boolean) => {
      if (!confirmado) {
        return;
      }
      this.documentosService.excluir(doc.hash).subscribe({
        next: () => {
          if (this.documentoAberto === doc) {
            this.fecharDocumento();
          }
          this.processo.documentos = (this.processo.documentos || []).filter((d: any) => d !== doc);
          this.snackBar.open('Documento excluído.', 'Fechar', { duration: 3000 });
        },
        error: (err) => {
          const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao excluir o documento.';
          this.snackBar.open(detalhe, 'Fechar', { duration: 4000 });
        },
      });
    });
  }

  private revogarObjectUrl(): void {
    if (this.objectUrlAtual) {
      URL.revokeObjectURL(this.objectUrlAtual);
      this.objectUrlAtual = null;
    }
  }

  public abrirDialogTramitar(): void {
    if (!this.processo) return;

    const idLocalAtual = this.processo.localAtual?.id;
    const locaisFiltrados = this.locaisDestino.filter((l) => l.id !== idLocalAtual);

    const ref = this.dialog.open(TramitarDialogComponent, {
      data: {
        locais: locaisFiltrados,
        origemNome: this.processo.localAtual?.nome,
      },
      autoFocus: false,
    });

    ref.afterClosed().subscribe((result: TramitarDialogResult | null) => {
      if (!result) return;
      this.executarTramitacao(result);
    });
  }

  private executarTramitacao(result: TramitarDialogResult): void {
    const idProcesso = this.processo?.idBeneficio ?? this.processo?.id;
    if (idProcesso == null) {
      this.snackBar.open('ID do processo não encontrado', 'Fechar', { duration: 4000 });
      return;
    }

    const idUsuario =
      this.sessionService.guardiao?.idUsuario ??
      this.sessionService.guardiao?.id ??
      null;

    this.processoService
      .tramitarProcesso(idProcesso, {
        idLocalDestino: result.idLocalDestino,
        observacao: result.observacao,
        idUsuario,
      })
      .subscribe({
        next: () => {
          this.snackBar.open('Processo tramitado com sucesso', 'Fechar', {
            duration: 4000,
          });
          this.carregarProcesso(String(idProcesso));
        },
        error: (err) => {
          const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao tramitar processo';
          this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
        },
      });
  }

  public excluirTramitacao(tramitacao: any): void {
    const motivo=window.prompt('Motivo do cancelamento da última tramitação:');
    if(!motivo?.trim()) return;
    const id=this.processo?.idBeneficio ?? this.processo?.id;
    this.processoService.cancelarTramitacao(id,tramitacao.id,motivo.trim()).subscribe({
      next:()=>this.carregarProcesso(String(id)),
      error:err=>this.snackBar.open(err.error?.detail ?? err.error?.message ?? 'Não foi possível cancelar a tramitação.','Fechar',{duration:6000})
    });
  }

  public verComposicaoOriginal():void {
    this.documentoAberto={nomeArquivo:'Discriminativo original da abertura'};
    this.carregandoDocumento=true;
    this.processoService.discriminativoOriginal(this.processo.idBeneficio).subscribe({
      next:blob=>{this.documentoBlob=blob;this.carregandoDocumento=false;},
      error:async err=>{this.fecharDocumento();let msg='Não foi possível emitir a composição original.';
        if(err.error instanceof Blob){try{const body=JSON.parse(await err.error.text());msg=body.detail||body.message||msg;}catch{}}
        this.snackBar.open(msg,'Fechar',{duration:7000});}
    });
  }

  public getDocumentosPendentesAssinatura(): any[] {
    const docs: any[] = this.processo?.documentos ?? [];
    return docs.filter((d) => d.hash && !d.assinado);
  }

  public async assinarTodos(documento?: any): Promise<void> {
    const pendentes = documento ? this.getDocumentosPendentesAssinatura().filter(d => d.hash === documento.hash) : this.getDocumentosPendentesAssinatura();
    if (pendentes.length === 0) {
      this.snackBar.open('Nenhum documento pendente de assinatura.', 'Fechar', {
        duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom',
      });
      return;
    }

    // Confirmação antes de iniciar a assinatura em lote.
    const confirmado = await firstValueFrom(
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          titulo: 'Assinar documentos',
          mensagem: `Confirma a assinatura de ${pendentes.length} documento(s)? ` +
            'Será solicitado o certificado digital.',
          textoConfirmar: 'Assinar',
        },
        autoFocus: false,
      }).afterClosed()
    );
    if (!confirmado) return;

    this.assinandoLote = true;
    this.progressoAssinatura = { atual: 0, total: pendentes.length };

    try {
      const resumo = await this.assinaturaLoteService.assinarLote(
        pendentes,
        (p) => (this.progressoAssinatura = p)
      );
      if (!resumo) return;

      // Com falhas, o aviso traz os motivos distintos devolvidos pelo servidor.
      this.snackBar.open(mensagemResumoAssinatura(resumo), 'Fechar', {
        duration: resumo.falha.length === 0 ? 5000 : 10000,
        horizontalPosition: 'right', verticalPosition: 'bottom',
      });

      // Recarrega para refletir os hashes assinados retornados pelo backend
      // (o sumiço antigo do documento foi corrigido no GET /completo, que agora
      // devolve hashAssinado e filtra documentos ativos).
      const idProcesso = this.processo?.idBeneficio ?? this.processo?.id;
      if (idProcesso != null) {
        this.carregarProcesso(String(idProcesso));
      }
    } catch (err: any) {
      this.snackBar.open(
        err?.message ?? 'Falha ao acessar certificados.',
        'Fechar',
        { duration: 6000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } finally {
      this.assinandoLote = false;
    }
  }

  // --- Aba Peças ---------------------------------------------------------------

  /**
   * Pela especificação, a aba Peças só é habilitada quando o processo está
   * aberto e já tramitado para a setorial.
   */
  public get pecasHabilitadas(): boolean {
    if (!this.processo) return false;
    const aberto = !!(this.processo.numeroProcesso || this.processo.dataAbertura);
    const tramitadoParaSetorial = (this.processo.tramitacoes ?? []).some((t: any) =>
      !t.cancelada && String(t.localDestinoNome ?? '').toUpperCase().includes('SETORIAL')
    );
    return aberto && tramitadoParaSetorial;
  }

  public arrastandoPeca = false;

  public onArquivoPecaSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pecaArquivo = input.files?.[0] ?? null;
    input.value = '';
  }

  public onDragOverPeca(event: DragEvent): void {
    event.preventDefault();
    this.arrastandoPeca = true;
  }

  public onDragLeavePeca(event: DragEvent): void {
    event.preventDefault();
    this.arrastandoPeca = false;
  }

  public onDropPeca(event: DragEvent): void {
    event.preventDefault();
    this.arrastandoPeca = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.pecaArquivo = file;
  }

  public removerArquivoPeca(event: Event): void {
    event.stopPropagation();
    this.pecaArquivo = null;
  }

  public formatarTamanho(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public inserirPeca(): void {
    if (!this.pecaTipoSelecionado) {
      this.snackBar.open('Selecione o tipo do documento.', 'Fechar', { duration: 4000 });
      return;
    }
    if (!this.pecaArquivo) {
      this.snackBar.open('Selecione um arquivo do computador.', 'Fechar', { duration: 4000 });
      return;
    }
    if (this.pecaArquivo.type !== 'application/pdf') {
      this.snackBar.open('A peça deve ser um arquivo PDF.', 'Fechar', { duration: 4000 });
      return;
    }

    this.inserindoPeca = true;
    this.analiseProcessoService
      .inserirPeca(this.pecaArquivo, this.pecaTipoSelecionado, this.processo)
      .subscribe({
        next: (res: any) => {
          this.pecaArquivo = null;
          this.pecaTipoSelecionado = null;
          this.inserindoPeca = false;
          this.snackBar.open(
            res?.message ?? 'Peça inserida no processo com sucesso.',
            'Fechar',
            { duration: 4000 }
          );
          // Recarrega do backend para a lista refletir o documento persistido (com hash).
          const idProcesso = this.processo?.idBeneficio ?? this.processo?.id;
          if (idProcesso != null) this.carregarProcesso(String(idProcesso));
        },
        error: (err) => {
          this.inserindoPeca = false;
          const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao inserir a peça.';
          this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
        },
      });
  }

  // --- Aba Análise Cearaprev -----------------------------------------------------

  private readonly PERFIS_ANALISE = ['CEARAPREVGESTOR', 'GESTORCPP', 'CEARAPREVGESTORCPP'];

  /** A aba só aparece para os perfis "Cearaprev Gestor" e "Gestor CPP". */
  public get podeVerAnaliseCearaprev(): boolean {
    const guardiao: any = this.sessionService.guardiao;
    const perfil = String(
      guardiao?.grupo?.nome ?? guardiao?.Grupo?.Nome ?? guardiao?.Grupo?.nome ?? guardiao?.perfil ?? ''
    ).toUpperCase().replace(/[^A-Z]/g, '');

    // Regra anterior a 21/09: sem perfil na sessão a aba fica visível; perfil por "contém".
    // TODO: quando o perfil vier consistente na sessão, remover o fallback.
    if (!perfil) return true;
    return this.PERFIS_ANALISE.some((p) => perfil.includes(p));
  }

  public salvarAnalise(): void {
    const idBeneficio = this.processo?.idBeneficio ?? this.processo?.id;
    if (idBeneficio == null) return;

    if (!this.analiseStatus) {
      this.snackBar.open('Selecione um status antes de salvar.', 'Fechar', { duration: 4000 });
      return;
    }

    const guardiao: any = this.sessionService.guardiao;
    const usuarioResponsavel: string =
      guardiao?.nome ?? guardiao?.Nome ?? guardiao?.login ?? 'Usuário do sistema';

    this.salvandoAnalise = true;
    this.analiseProcessoService
      .atualizarAnalise(idBeneficio, this.analiseStatus, usuarioResponsavel)
      .subscribe({
        next: (res: any) => {
          this.salvandoAnalise = false;
          // O endpoint devolve o detalhe completo atualizado (com o histórico novo).
          if (res?.idBeneficio != null) {
            this.processo = res;
            this.analiseStatus = res?.statusBeneficio ?? this.analiseStatus;
            this.analiseDataPublicacao = this.dataParaInput(res?.dataPublicacaoDO);
            this.analiseDataRegistroTce = this.dataParaInput(res?.dataRegistroTCE);
          }
          this.snackBar.open('Status incluído com sucesso.', 'Fechar', { duration: 4000 });
        },
        error: (err) => {
          this.salvandoAnalise = false;
          const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao atualizar os dados da análise.';
          this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
        },
      });
  }

  public publicarAtoEdweb(): void {
    const idBeneficio = this.processo?.idBeneficio ?? this.processo?.id;
    if (idBeneficio == null) return;

    this.analiseProcessoService.publicarAtoEdweb(idBeneficio).subscribe({
      next: () => this.snackBar.open('Ato enviado para publicação no Edweb.', 'Fechar', { duration: 4000 }),
      error: (err) => {
        const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao publicar o ato no Edweb.';
        this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
      },
    });
  }

  public gerarAtoSemEfeito(): void {
    const idBeneficio = this.processo?.idBeneficio ?? this.processo?.id;
    if (idBeneficio == null) return;

    this.analiseProcessoService.gerarAtoSemEfeito(idBeneficio).subscribe({
      next: () => this.snackBar.open('Ato sem efeito gerado.', 'Fechar', { duration: 4000 }),
      error: (err) => {
        const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao gerar o ato sem efeito.';
        this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
      },
    });
  }

  public abrirDialogDatasAtoSemEfeito(): void {
    const idBeneficio = this.processo?.idBeneficio ?? this.processo?.id;
    if (idBeneficio == null) return;

    const ref = this.dialog.open(DatasAtoSemEfeitoDialogComponent, { autoFocus: false, data:{dataAto:this.dataParaInput(this.processo?.dataAtoSemEfeito),dataPublicacao:this.dataParaInput(this.processo?.dataPublicacaoSemEfeito)} });

    ref.afterClosed().subscribe((datas) => {
      if (!datas) return;
      this.analiseProcessoService.adicionarDatasAtoSemEfeito(idBeneficio, datas).subscribe({
        next: () => { this.carregarProcesso(String(idBeneficio)); this.snackBar.open('Datas do ato sem efeito salvas.', 'Fechar', { duration: 4000 }); },
        error: (err) => {
          const detalhe = err?.error?.detail ?? err?.error?.message ?? 'Falha ao salvar as datas do ato sem efeito.';
          this.snackBar.open(detalhe, 'Fechar', { duration: 5000 });
        },
      });
    });
  }
}
